import React, { useState } from "react";
import { Button } from "antd";
import {
  CloseCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import BaseTable from "../../../../components/BaseTable";
import Column from "../../../../components/Column";
import Row from "../../../../components/Row";
import AppInputText from "../../../../components/AppInputText/AppInputText";
import ActionHeader from "../../../../components/ActionHeader";
import usePageState from "../../../../hooks/usePageState";
import { useGlobalState } from "../../../../context/AppGlobalContext";
import useConfirmAction from "../../../../hooks/useConfirmAction";
import {
  CreateRawMaterialRequest,
  RawMaterialModel,
} from "../../../../../infrastructure/model/RawMaterialModel";
import {
  deleteRawMaterialUseCase,
  getListRawMaterialUseCase,
  insertRawMaterialUseCase,
} from "../../../../../applications/useCase/ApiUseCase/RawMaterialApiUseCase";
import useForm from "../../../../hooks/useForm";
import * as Yup from "yup";
import AppInputDropdown from "../../../../components/AppInputDropdown";
import Visible from "../../../../components/Visible";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";

function RawMaterialManager() {
  const [dataTable, setDataTable] = useState<RawMaterialModel[]>([]);
  const [itemSelected, setItemSelected] = useState<RawMaterialModel>();
  const { dialog: confirmDeleteDialog, confirm: confirmDelete } = useConfirmAction({
    onConfirm: deleteRawMaterial,
  });
  const { isLoading, setLoading, showError, showSuccess, showClientError } = usePageState();
  const { listUoM } = useGlobalState();

  const createRawMaterialSchema = Yup.object({
    uom_id: Yup.number()
      .typeError("uom_id phải là số")
      .required("uom_id là bắt buộc")
      .moreThan(0, "uom_id phải > 0"),

    code: Yup.string().required("code là bắt buộc").trim(),

    name: Yup.string().required("name là bắt buộc").trim(),
  });

  const formik = useForm<CreateRawMaterialRequest>({
    initialValues: {
      code: "",
      name: "",
      description: "",
      uom_id: 0,
    },
    validationSchema: createRawMaterialSchema,
    onSubmit: async (values, formikHelpers) => {
      const result = await insertRawMaterialUseCase({
        id: itemSelected?.id,
        request: values,
        setLoading,
        showError,
        showSuccess,
      });
      if (result) {
        setDataTable([result, ...dataTable]);
        formik.resetForm();
      }
    },
  });

  async function getListData() {
    const dataRes = await getListRawMaterialUseCase({
      setLoading,
      showError,
      showSuccess,
    });
    setDataTable(dataRes);
    setItemSelected(undefined);
    formik.resetForm();
  }

  async function deleteRawMaterial() {
    if (!itemSelected?.id) return;
    const dataRes = await deleteRawMaterialUseCase({
      id: itemSelected.id,
      setLoading,
      showError,
      showSuccess,
    });
    if (dataRes) {
      setDataTable(dataTable.filter((e) => e.id !== itemSelected.id));
      setItemSelected(undefined);
    }
  }

  useAsyncEffect(async () => await getListData(), []);

  return (
    <Column className="h-100 w-100 p-3">
      <Column className="gap-2 pb-2">
        <Row className="w-100 gap-2">
          <Column className="col-6">
            <AppInputText
              orientation="col"
              label="Mã NVL"
              additionProps={{
                placeholder: "NVL1, ...",
                disabled: !!itemSelected?.id,
              }}
              isRequired
              currentValue={formik.values.code}
              onTextChange={(e) => formik.updateFieldValue("code", e)}
              errorText={formik.getFormErrorMessage("code")}
            />
          </Column>
          <Column className="col-6">
            <AppInputText
              orientation="col"
              label="Tên NVL"
              additionProps={{
                placeholder: "NVL1, ...",
              }}
              isRequired
              currentValue={formik.values.name}
              onTextChange={(e) => formik.updateFieldValue("name", e)}
              errorText={formik.getFormErrorMessage("name")}
            />
          </Column>
          <Column className="col-3">
            <AppInputDropdown
              orientation="col"
              label="ĐVT"
              options={listUoM?.map((e) => ({
                label: e.name,
                value: e.id,
              }))}
              disabled={!!itemSelected?.id}
              isRequired
              currentValue={formik.values.uom_id}
              onChangeValue={(e) => formik.updateFieldValue("uom_id", e)}
              errorText={formik.getFormErrorMessage("uom_id")}
            />
          </Column>
          <Column className="col-6">
            <AppInputText
              orientation="col"
              label="Ghi chú"
              currentValue={formik.values.description}
              onTextChange={(e) => formik.updateFieldValue("description", e)}
            />
          </Column>
          <Button
            type="primary"
            htmlType="submit"
            icon={<PlusOutlined />}
            style={{ marginTop: "19px" }}
            onClick={() => formik.checkErrorAndSubmit(showClientError)}
          >
            {itemSelected?.id ? "Sửa" : "Thêm"} NVL
          </Button>
          <Visible isVisible={!!itemSelected?.id}>
            <Button
              icon={<CloseCircleOutlined />}
              style={{ marginTop: "19px" }}
              onClick={() => {
                formik.resetForm();
                setItemSelected(undefined);
              }}
              color="danger"
              variant="solid"
            >
              Hủy
            </Button>
          </Visible>
        </Row>

        {/* Danh sách Table */}
        <ActionHeader
          rowCount={dataTable?.length}
          actions={[
            {
              icon: <ReloadOutlined />,
              userClick: getListData,
              title: "Làm mới",
              isNormal: true,
            },
            {
              icon: <DeleteOutlined />,
              userClick: confirmDelete,
              title: "Xóa",
              disabled: !itemSelected?.id,
              isNormal: true,
              color: "danger",
            },
          ]}
        />
      </Column>

      <BaseTable
        dataSource={dataTable}
        rowSelection={{
          type: "radio",
          selectedRowKeys: itemSelected?.id ? [itemSelected.id] : [],
          onSelect(record) {
            setItemSelected(record);
            const { uom, ...request } = record;
            formik.setValues({
              ...request,
              uom_id: record.uom.id,
            });
          },
        }}
        loading={isLoading}
        columns={[
          {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: "10%",
          },
          {
            title: "Mã NVL",
            dataIndex: "code",
            key: "code",
          },
          {
            title: "Tên NVL",
            dataIndex: "name",
            key: "name",
          },
          {
            title: "ĐVT",
            dataIndex: "uom",
            key: "uom",
            render(value, record, index) {
              return value?.name;
            },
          },
          {
            title: "Ghi chú",
            dataIndex: "description",
            key: "description",
          },
        ]}
        rowKey="id"
      />
      {confirmDeleteDialog}
    </Column>
  );
}

export default RawMaterialManager;
